import React, { useState } from 'react';
import { Drawer, Box, Typography, IconButton, Divider, Chip, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Accordion, AccordionSummary, AccordionDetails, Grid, Paper, Card, CardContent, CircularProgress, Alert, useTheme, useMediaQuery } from '@mui/material';
import { Icon } from '@iconify/react';
import { useToast } from './ui/ToastProvider';
interface ComplianceTransaction {
  txnId: string;
  mtoName: string;
  payoutType: 'FT' | 'IBFT' | 'OTC';
  beneficiaryName: string;
  beneficiaryCnic: string;
  beneficiaryTitle: string;
  amountFcy: string;
  currencyFcy: string;
  amountPkr: string;
  createdAt: string;
  lastUpdateAt: string;
  lifecycle: 'IN_REVIEW';
  currentState: 'SCREENING';
  mtoRef: string;
  bankRef: string;
  railRefs: {
    RaastMsgId?: string;
    RRN?: string;
    STAN?: string;
    UETR?: string;
  };
  screening: {
    engine: string;
    score: number;
    hits: string[];
    failReasonCode: string;
    failReasonText: string;
    screenedAt: string;
  };
  remitterName: string;
  remitterIdType: string;
  remitterIdNumber: string;
  remitterCountry: string;
  beneficiaryIban?: string;
  beneficiaryBank?: string;
  purposeCode: string;
  purposeText: string;
  fxRate: number;
}
interface CBSBeneficiaryDetails {
  cnic: string;
  fatherName: string;
  dob: string;
  accountOpeningDate: string;
  accountStatus: string;
}
interface ComplianceDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  transaction: ComplianceTransaction;
  onTransactionRemoval: (txnId: string) => void;
}
const ComplianceDetailsDrawer: React.FC<ComplianceDetailsDrawerProps> = ({
  open,
  onClose,
  transaction,
  onTransactionRemoval
}) => {
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'release' | 'fail'>('release');
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [cbsDetails, setCbsDetails] = useState<CBSBeneficiaryDetails | null>(null);
  const [cbsLoading, setCbsLoading] = useState(false);
  const [cbsError, setCbsError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    showToast
  } = useToast();
  const handleOpenActionDialog = (type: 'release' | 'fail') => {
    setActionType(type);
    setComment('');
    setCommentError('');
    setActionDialogOpen(true);
  };
  const handleCloseActionDialog = () => {
    setActionDialogOpen(false);
    setComment('');
    setCommentError('');
  };
  const validateComment = () => {
    if (!comment.trim()) {
      setCommentError('Comment is required');
      return false;
    }
    if (comment.trim().length < 10) {
      setCommentError('Comment must be at least 10 characters');
      return false;
    }
    setCommentError('');
    return true;
  };
  const handleActionConfirm = async () => {
    if (!validateComment()) return;
    setIsProcessing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      if (actionType === 'release') {
        showToast('Transaction released successfully', 'success');
      } else {
        showToast('Transaction marked as failed', 'success');
      }
      onTransactionRemoval(transaction.txnId);
      handleCloseActionDialog();
    } catch (error) {
      showToast('Action failed. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };
  const handleFetchCBSDetails = async () => {
    setCbsLoading(true);
    setCbsError('');
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      // Mock CBS response
      const mockCBSDetails: CBSBeneficiaryDetails = {
        cnic: transaction.beneficiaryCnic,
        fatherName: 'Father Name Example',
        dob: '1985-06-15',
        accountOpeningDate: '2015-03-20',
        accountStatus: 'ACTIVE'
      };
      setCbsDetails(mockCBSDetails);
      showToast('Beneficiary details fetched successfully', 'success');
    } catch (error) {
      setCbsError('Failed to fetch beneficiary details from CBS');
      showToast('Failed to fetch CBS details', 'error');
    } finally {
      setCbsLoading(false);
    }
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };
  const getPayoutTypeColor = (type: string) => {
    const colors = {
      FT: {
        bg: 'rgba(14, 165, 233, 0.1)',
        color: '#0EA5E9'
      },
      IBFT: {
        bg: 'rgba(139, 92, 246, 0.1)',
        color: '#8B5CF6'
      },
      OTC: {
        bg: 'rgba(245, 158, 11, 0.1)',
        color: '#F59E0B'
      }
    };
    return colors[type as keyof typeof colors];
  };
  return <>
      <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
      sx: {
        width: isMobile ? '100%' : 560,
        maxWidth: '100%'
      }
    }} data-id="element-742" data-mp="j0f9pq2kikmg">
        <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }} data-id="element-743" data-mp="2lpfd2nan1euw">
          {/* Header */}
          <Box sx={{
          p: 3,
          pb: 2
        }} data-id="element-744" data-mp="2qq34vb6oxzq6">
            <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2
          }} data-id="element-745" data-mp="2uo0uug8acpum">
              <Typography variant="h6" data-id="element-746" data-mp="26zzrdlpyrzm2">Compliance Review</Typography>
              <IconButton onClick={onClose} size="small" data-id="element-747" data-mp="26y6is0vsj5bf">
                <Icon icon="lucide:x" width={20} data-id="element-748" data-mp="2byb17wg9ea4a" />
              </IconButton>
            </Box>
            {/* Header Chips */}
            <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2
          }} data-id="element-749" data-mp="2grnfpdr850jb">
              <Chip label={transaction.payoutType} size="small" sx={{
              ...getPayoutTypeColor(transaction.payoutType),
              fontWeight: 600
            }} data-id="element-750" data-mp="1o8gorfzuqnkk" />
              <Chip label="IN_REVIEW" size="small" sx={{
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              color: '#F59E0B',
              fontWeight: 600
            }} data-id="element-751" data-mp="30czeexdx44p7" />
              <Chip label="SCREENING" size="small" sx={{
              backgroundColor: 'rgba(100, 116, 139, 0.1)',
              color: '#64748B',
              fontWeight: 600
            }} data-id="element-752" data-mp="28nxdill5ihtv" />
              <Chip label={`${transaction.currencyFcy} ${transaction.amountFcy}`} size="small" sx={{
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              color: '#10B981',
              fontWeight: 600
            }} data-id="element-753" data-mp="2cmn116zwlfr8" />
              <Chip label={`PKR ${transaction.amountPkr}`} size="small" sx={{
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              color: '#10B981',
              fontWeight: 600
            }} data-id="element-754" data-mp="cwjyo7eigk0" />
            </Box>
            {/* Subheader */}
            <Box data-id="element-755" data-mp="j4xtckhsxp7o">
              <Typography variant="subtitle2" color="text.secondary" gutterBottom data-id="element-756" data-mp="j4fo4yd5w0nm">
                Remitting Institute (MTO)
              </Typography>
              <Typography variant="h6" color="primary.main" gutterBottom data-id="element-757" data-mp="9e34r9h32dj2">
                {transaction.mtoName}
              </Typography>
              <Typography variant="body2" color="text.secondary" data-id="element-758" data-mp="ebmim89vcoah">
                MTO Ref: {transaction.mtoRef} • Bank Ref: {transaction.bankRef}
              </Typography>
              {Object.values(transaction.railRefs).some(v => v) && <Typography variant="body2" color="text.secondary" data-id="element-759" data-mp="32xojkmhdgip6">
                  Rail Refs:{' '}
                  {[transaction.railRefs.RaastMsgId && `Raast: ${transaction.railRefs.RaastMsgId}`, transaction.railRefs.RRN && `RRN: ${transaction.railRefs.RRN}`, transaction.railRefs.STAN && `STAN: ${transaction.railRefs.STAN}`, transaction.railRefs.UETR && `UETR: ${transaction.railRefs.UETR}`].filter(Boolean).join(' • ')}
                </Typography>}
            </Box>
          </Box>
          <Divider data-id="element-760" data-mp="37w854ls1zsh0" />
          {/* Content */}
          <Box sx={{
          flex: 1,
          overflow: 'auto',
          p: 3
        }} data-id="element-761" data-mp="21gysed3sbkty">
            {/* Screening Summary */}
            <Accordion defaultExpanded data-id="element-762" data-mp="1wjp3cykb5b6n">
              <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-764" data-mp="bx7c902xesgy" />} data-id="element-763" data-mp="c0czsavwpmak">
                <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }} data-id="element-765" data-mp="gywky92oeu1v">
                  <Icon icon="lucide:shield-alert" width={20} color="#F59E0B" data-id="element-766" data-mp="pri1diiu4onj" />
                  <Typography variant="subtitle2" fontWeight={600} data-id="element-767" data-mp="uoqbqwxqroe8">
                    Screening Summary
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails data-id="element-768" data-mp="14npmqm5zbw4e">
                <Alert severity="warning" sx={{
                mb: 2
              }} data-id="element-769" data-mp="14oh6mzq7s5eu">
                  <Typography variant="body2" fontWeight={600} data-id="element-770" data-mp="36fm081bpjg1t">
                    Status: REVIEW REQUIRED
                  </Typography>
                  <Typography variant="body2" sx={{
                  mt: 1
                }} data-id="element-771" data-mp="3ahpdmvhxsupq">
                    {transaction.screening.failReasonText}
                  </Typography>
                </Alert>
                <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }} data-id="element-772" data-mp="3ujoxj5jp58e3">
                  {transaction.screening.hits.map((hit, index) => <Paper key={index} variant="outlined" sx={{
                  p: 1.5,
                  backgroundColor: '#FEF3C7',
                  borderColor: '#F59E0B'
                }} data-id="element-773" data-mp="cec03ql7qd4t" data-mpi={index}>
                      <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }} data-id="element-774" data-mp="r3hv12d0ph6w">
                        <Icon icon="lucide:alert-circle" width={16} color="#F59E0B" data-id="element-775" data-mp="2gleuxedjv5m5" />
                        <Typography variant="body2" data-id="element-776" data-mp="26le7bg8ne3c5">{hit}</Typography>
                      </Box>
                    </Paper>)}
                </Box>
              </AccordionDetails>
            </Accordion>
            {/* Parties */}
            <Accordion data-id="element-777" data-mp="2zcxrb2d2jlf7">
              <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-779" data-mp="2ud35vec2bo1j" />} data-id="element-778" data-mp="2uc1rp0zr2lfl">
                <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }} data-id="element-780" data-mp="2pka14qglgfpg">
                  <Icon icon="lucide:users" width={20} color="#0EA5E9" data-id="element-781" data-mp="3i1mbfm3q9mpx" />
                  <Typography variant="subtitle2" fontWeight={600} data-id="element-782" data-mp="3damguni2zgql">
                    Parties
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails data-id="element-783" data-mp="1bf6rli5qkd2w">
                <Typography variant="subtitle2" color="primary.main" gutterBottom data-id="element-784" data-mp="1gcosqmdiho0c">
                  Remitter
                </Typography>
                <Paper variant="outlined" sx={{
                p: 2,
                mb: 2,
                borderRadius: 2
              }} data-id="element-785" data-mp="16llt7p08dh36">
                  <Typography variant="body1" fontWeight={600} data-id="element-786" data-mp="wp8xkll4kt5s">
                    {transaction.remitterName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{
                  mt: 1
                }} data-id="element-787" data-mp="iqrgv7tn0gxd">
                    {transaction.remitterIdType}: {transaction.remitterIdNumber}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" data-id="element-788" data-mp="6dxi2axlswkq">
                    Country: {transaction.remitterCountry}
                  </Typography>
                </Paper>
                <Typography variant="subtitle2" color="primary.main" gutterBottom data-id="element-789" data-mp="1d9i2zred5ed">
                  Beneficiary
                </Typography>
                <Paper variant="outlined" sx={{
                p: 2,
                borderRadius: 2
              }} data-id="element-790" data-mp="p4oq995uugxj">
                  <Typography variant="body1" fontWeight={600} data-id="element-791" data-mp="k7y5w9n0oz6b">
                    {transaction.beneficiaryName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{
                  mt: 1
                }} data-id="element-792" data-mp="u4t4stn7d1l4">
                    CNIC: {transaction.beneficiaryCnic}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" data-id="element-793" data-mp="1myzcc195eocm">
                    Title: {transaction.beneficiaryTitle}
                  </Typography>
                  {transaction.beneficiaryIban && <Typography variant="body2" color="text.secondary" data-id="element-794" data-mp="1wxowy1sfimwk">
                      IBAN: {transaction.beneficiaryIban}
                    </Typography>}
                  {transaction.beneficiaryBank && <Typography variant="body2" color="text.secondary" data-id="element-795" data-mp="145lsar6z42bs">
                      Bank: {transaction.beneficiaryBank}
                    </Typography>}
                </Paper>
              </AccordionDetails>
            </Accordion>
            {/* Amounts & Purpose */}
            <Accordion data-id="element-796" data-mp="1i17n5ezw1gc4">
              <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-798" data-mp="lgfbj9pqpksm" />} data-id="element-797" data-mp="lhgppn21ynek">
                <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }} data-id="element-799" data-mp="pi0qr1pqyb5z">
                  <Icon icon="lucide:banknote" width={20} color="#10B981" data-id="element-800" data-mp="czya3xsjrhp7" />
                  <Typography variant="subtitle2" fontWeight={600} data-id="element-801" data-mp="8yl6lucxfqlg">
                    Amounts & Purpose
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails data-id="element-802" data-mp="3v5t46ahlut6x">
                <Grid container spacing={2} data-id="element-803" data-mp="3v62ujz6kb2d5">
                  <Grid item xs={12} sm={6} data-id="element-804" data-mp="3v7w3jl4ndyob">
                    <Typography variant="caption" color="text.secondary" data-id="element-805" data-mp="3qekxvnbknnxz">
                      Foreign Currency
                    </Typography>
                    <Typography variant="h6" color="primary.main" data-id="element-806" data-mp="3lg9jr0e8ur9y">
                      {transaction.currencyFcy} {transaction.amountFcy}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} data-id="element-807" data-mp="3cejthxw3lydr">
                    <Typography variant="caption" color="text.secondary" data-id="element-808" data-mp="37fp0y48bwvgg">
                      PKR Amount
                    </Typography>
                    <Typography variant="h6" color="primary.main" data-id="element-809" data-mp="2xr1m2rr5ipnn">
                      PKR {transaction.amountPkr}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-810" data-mp="2nv8eznw7bod9">
                    <Typography variant="caption" color="text.secondary" data-id="element-811" data-mp="2nw9nplpqvyrt">
                      Purpose
                    </Typography>
                    <Typography variant="body1" data-id="element-812" data-mp="3gjdzqan99dd3">
                      {transaction.purposeText} ({transaction.purposeCode})
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
            {/* Timeline */}
            <Accordion data-id="element-813" data-mp="3bse2f4a84ta2">
              <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-815" data-mp="3bqkvdnvp871j" />} data-id="element-814" data-mp="3brvzjot23gt6">
                <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }} data-id="element-816" data-mp="3bspjceh1g1o">
                  <Icon icon="lucide:clock" width={20} color="#6366F1" data-id="element-817" data-mp="1ssob9b2oy1uz" />
                  <Typography variant="subtitle2" fontWeight={600} data-id="element-818" data-mp="1nv4ua3elzazc">
                    Timeline
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails data-id="element-819" data-mp="1xkfzheydjvhy">
                <Box sx={{
                position: 'relative',
                pl: 3
              }} data-id="element-820" data-mp="1xk6c7z4ps2fy">
                  <Box sx={{
                  position: 'absolute',
                  left: 8,
                  top: 8,
                  bottom: 8,
                  width: 2,
                  backgroundColor: '#E2E8F0'
                }} data-id="element-821" data-mp="19wonydknrmhq" />
                  <Box sx={{
                  mb: 2,
                  position: 'relative'
                }} data-id="element-822" data-mp="2uaoysz7e957a">
                    <Box sx={{
                    position: 'absolute',
                    left: -19,
                    top: 4,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#10B981',
                    border: '2px solid white'
                  }} data-id="element-823" data-mp="2q8u1s6whxdzb" />
                    <Typography variant="caption" color="text.secondary" data-id="element-824" data-mp="16cxzvh7vy0w1">
                      {formatDate(transaction.createdAt)}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} data-id="element-825" data-mp="1ba692sb22ul5">
                      Transaction Received
                    </Typography>
                  </Box>
                  <Box sx={{
                  position: 'relative'
                }} data-id="element-826" data-mp="1lagjy69mbpx5">
                    <Box sx={{
                    position: 'absolute',
                    left: -19,
                    top: 4,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#F59E0B',
                    border: '2px solid white'
                  }} data-id="element-827" data-mp="3i4bd6x66p4hi" />
                    <Typography variant="caption" color="text.secondary" data-id="element-828" data-mp="1qfao4umx3yu9">
                      {formatDate(transaction.screening.screenedAt)}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} data-id="element-829" data-mp="2n37540m9ow60">
                      Screening (REVIEW)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontSize="0.8rem" data-id="element-830" data-mp="2e43kutbd6apx">
                      Flagged for manual review
                    </Typography>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
            {/* CBS Details (FT only) */}
            {transaction.payoutType === 'FT' && <Box sx={{
            mt: 3
          }} data-id="element-831" data-mp="19fl5ipscr6j5">
                <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2
            }} data-id="element-832" data-mp="14kp08ny3umo3">
                  <Icon icon="lucide:building-2" width={20} color="#6366F1" data-id="element-833" data-mp="1sccjqkt1evgc" />
                  <Typography variant="subtitle2" fontWeight={600} data-id="element-834" data-mp="1sa9wg8j9rgfb">
                    CBS Beneficiary Details
                  </Typography>
                </Box>
                {!cbsDetails && !cbsError && <Button variant="outlined" fullWidth startIcon={cbsLoading ? <CircularProgress size={16} data-id="element-836" data-mp="70kw6s0ky5up" /> : <Icon icon="lucide:download" width={16} data-id="element-837" data-mp="1yvn3hwx4299" />} onClick={handleFetchCBSDetails} disabled={cbsLoading} data-id="element-835" data-mp="1ifztjwwpazqc">
                    {cbsLoading ? 'Fetching...' : 'Fetch Beneficiary Details from CBS'}
                  </Button>}
                {cbsError && <Alert severity="error" sx={{
              mb: 2
            }} data-id="element-838" data-mp="2se9b3kl8tt29">
                    {cbsError}
                  </Alert>}
                {cbsDetails && <Card variant="outlined" sx={{
              backgroundColor: '#F8FAFC'
            }} data-id="element-839" data-mp="36d8zqs8ra7yf">
                    <CardContent data-id="element-840" data-mp="1bypyeszyaahz">
                      <Grid container spacing={2} data-id="element-841" data-mp="1bzr8ov956syn">
                        <Grid item xs={12} sm={6} data-id="element-842" data-mp="1gwhmfbxh5cgw">
                          <Typography variant="caption" color="text.secondary" data-id="element-843" data-mp="1gx9jkr6pzi9a">
                            CNIC
                          </Typography>
                          <Typography variant="body2" fontFamily="monospace" data-id="element-844" data-mp="14v8c4ir02h8d">
                            {cbsDetails.cnic}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} data-id="element-845" data-mp="1duvcr2bwnj0d">
                          <Typography variant="caption" color="text.secondary" data-id="element-846" data-mp="1du3fln2ntd7z">
                            Father Name
                          </Typography>
                          <Typography variant="body2" data-id="element-847" data-mp="1nudst7op2kn5">
                            {cbsDetails.fatherName}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} data-id="element-848" data-mp="1wm6059if9dni">
                          <Typography variant="caption" color="text.secondary" data-id="element-849" data-mp="1wmfqiy7dpmtq">
                            Date of Birth
                          </Typography>
                          <Typography variant="body2" data-id="element-850" data-mp="26klox5tz8atu">
                            {new Date(cbsDetails.dob).toLocaleDateString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} data-id="element-851" data-mp="2u7z0vxxz4gb0">
                          <Typography variant="caption" color="text.secondary" data-id="element-852" data-mp="2u5cwu8aakixj">
                            Account Opening Date
                          </Typography>
                          <Typography variant="body2" data-id="element-853" data-mp="2l5hhdr9pe1rp">
                            {new Date(cbsDetails.accountOpeningDate).toLocaleDateString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} data-id="element-854" data-mp="1xhq2cfws3amq">
                          <Typography variant="caption" color="text.secondary" data-id="element-855" data-mp="1xj17aj1ymoff">
                            Account Status
                          </Typography>
                          <Chip label={cbsDetails.accountStatus} size="small" sx={{
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      color: '#10B981',
                      fontWeight: 600,
                      mt: 0.5
                    }} data-id="element-856" data-mp="1niqugzju7j0s" />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>}
              </Box>}
          </Box>
          <Divider data-id="element-857" data-mp="2c5tdtkjgvkki" />
          {/* Actions */}
          <Box sx={{
          p: 3,
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap'
        }} data-id="element-858" data-mp="283g82w4pzmnq">
            <Button variant="contained" fullWidth={isMobile} startIcon={<Icon icon="lucide:check" width={16} data-id="element-860" data-mp="1y6trwknc9js1" />} onClick={() => handleOpenActionDialog('release')} sx={{
            flex: isMobile ? undefined : 1
          }} data-id="element-859" data-mp="234vgfl3jabaw">
              Manually Release
            </Button>
            <Button variant="outlined" color="error" fullWidth={isMobile} startIcon={<Icon icon="lucide:x" width={16} data-id="element-862" data-mp="8ux3hky0es4k" />} onClick={() => handleOpenActionDialog('fail')} sx={{
            flex: isMobile ? undefined : 1
          }} data-id="element-861" data-mp="3y6l2qyqfkfz">
              Mark as Failed
            </Button>
          </Box>
        </Box>
      </Drawer>
      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={handleCloseActionDialog} maxWidth="sm" fullWidth PaperProps={{
      sx: {
        borderRadius: 2
      }
    }} data-id="element-863" data-mp="3h6b50in0izuc">
        <DialogTitle data-id="element-864" data-mp="3w1qt870ww1it">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-865" data-mp="3w1z4juhdyyrw">
            <Icon icon={actionType === 'release' ? 'lucide:check-circle' : 'lucide:x-circle'} width={24} color={actionType === 'release' ? '#10B981' : '#EF4444'} data-id="element-866" data-mp="1bayn3kyz4b9o" />
            <Typography variant="h6" data-id="element-867" data-mp="32h4oat5jj4qs">
              {actionType === 'release' ? 'Manually Release Transaction' : 'Mark Transaction as Failed'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-868" data-mp="2on3w6i93gfze">
          <Typography variant="body2" color="text.secondary" sx={{
          mb: 2
        }} data-id="element-869" data-mp="2ooeyseqszhox">
            {actionType === 'release' ? `Approve transaction ${transaction.txnId} for processing? This will bypass screening alerts.` : `Mark transaction ${transaction.txnId} as failed? This action will reject the transaction.`}
          </Typography>
          <TextField fullWidth multiline rows={4} label="Comment (Required)" placeholder="Enter your reason for this action (minimum 10 characters)..." value={comment} onChange={e => {
          setComment(e.target.value);
          if (commentError) setCommentError('');
        }} error={!!commentError} helperText={commentError || `${comment.length} characters`} required data-id="element-870" data-mp="24xjojb4qtdsy" />
        </DialogContent>
        <DialogActions sx={{
        p: 3,
        pt: 0
      }} data-id="element-871" data-mp="29k2h9ss3ml8q">
          <Button onClick={handleCloseActionDialog} disabled={isProcessing} data-id="element-872" data-mp="2ek8a3ca3udse">
            Cancel
          </Button>
          <Button variant="contained" color={actionType === 'release' ? 'primary' : 'error'} onClick={handleActionConfirm} disabled={isProcessing || !comment.trim() || comment.trim().length < 10} startIcon={isProcessing ? <CircularProgress size={16} data-id="element-874" data-mp="t75akwe1ta4k" /> : undefined} data-id="element-873" data-mp="1zlea5p1njqjt">
            {isProcessing ? 'Processing...' : actionType === 'release' ? 'Release' : 'Mark as Failed'}
          </Button>
        </DialogActions>
      </Dialog>
    </>;
};
export default ComplianceDetailsDrawer;